import { useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { useHistory, useLocation } from "react-router";
import useCurrentService from "../useCurrentService";
import _ from "lodash";
import { trackTiming, trackExternalLink, trackEvent, trackFullStoryEvent } from "../../utils/mrTracker";
import querystring from "query-string";
import { navigateToSystemManagerFromService } from "../systemManagerNavigationHelper";

const noListener = () => false;
export default ({ messagePrefix = "SERVICE", listener = noListener, shouldListen = true, trackKey, setWizardOpen, subTabs, featureId, scope, endpointId }) => {

    const initializationData = useRef({}); const history = useHistory();
    const initialUrlParams = useRef(querystring.parse(window.location.search));

    const apiResponses = useSelector(state => state.occmProxy?.responses);
    const { idTokenPayload: userMetadata, accessToken } = useSelector(state => state.auth);
    const { selectedAgent: connectorAgent, selectedAgentId: connectorId, selectedAccountId: accountId, selectedWorkspaceId: workspaceId, isDemo: isDemoMode, selectedOrgId: organizationId, selectedScopeId: scopeId } = useSelector(state => state.tenancy);
    const location = useLocation();
    const dispatch = useDispatch();
    const currentService = useCurrentService();
    const connectorName = connectorAgent?.name;
    const features = useSelector(state => ({
        byKey: state.features.byKey,
        active: state.features.active
    }), shallowEqual);

    let selection = undefined;

    if (location?.state?.selection) {
        if (location.state.selection?.workingEnvironments) {
            selection = {
                workingEnvironments: location.state.selection.workingEnvironments
            };
        } else if (location.state.selection?.workingEnvironment) {
            selection = {
                workingEnvironment: location.state.selection.workingEnvironment
            };
        }
    }

    const getBaseUrl = () => {
        const isParentTab = !_.has(currentService.uiIntegration, 'subTabs');
        if (isParentTab) return _.get(currentService, "service.to");
        const tabWithMfe = _.find(currentService.uiIntegration.stageIntegrations, stageIntegration => ((!_.isUndefined(scope) && stageIntegration.scope === scope) || stageIntegration.endpointId === endpointId));
        return `${_.get(currentService, "service.to")}${tabWithMfe.path}`;
    }

    const connectorIdFromLocation = location.state?.connectorId;

    initializationData.current = {
        userMetadata,
        search: location.search,
        ...location.state, //this can override the search
        accessToken: accessToken,
        accountId: accountId === "no-project" ? undefined : accountId,
        workspaceId,
        organizationId: organizationId || undefined,
        scopeId: scopeId || undefined,
        connectorId: connectorIdFromLocation || connectorId || undefined,
        agentId: location.pathname?.startsWith("/governance") ? connectorId?.substring(0, connectorId.length - 7) : (connectorId || undefined),
        isDemoMode: isDemoMode || false,
        fromService: location?.state?.fromService || "direct",
        fromAction: _.get(location, "state.action"),
        selection: selection ? window.btoa(JSON.stringify(selection)) : undefined,
        pathname: location.pathname,
        hash: location.hash,
        domain: window.location.host,
        features,
        subTabs,
        featureId,
        connectorName: connectorName,
        baseUrl: getBaseUrl(),
        urlParams: initialUrlParams.current,
        theme: features.active["Platform.BlueXP/DarkTheme"] ? "dark": "light"
    };

    const sendMessage = useCallback(({ type, payload }) => {
        window.postMessage({
            type: `${messagePrefix}:${type}`,
            payload
        });
    }, [messagePrefix]);

    useEffect(() => {
        if (shouldListen) {
            const receiveMessage = (event) => {
                if (_.isObject(event.data)) {
                    const { type, payload } = event.data;
                    switch (type?.substring(messagePrefix.length + 1)) {
                        case "SERVICE:READY":
                        case "READY": {
                            return;
                        }
                        case "SERVICE:NAVIGATE":
                        case "NAVIGATE": {
                            if (payload.hash) {
                                history.replace({
                                    ...history.location,
                                    hash: payload.hash
                                })
                            } else {
                                history.push(payload.pathname, {
                                    ...payload.state,
                                    fromService: "-",
                                    action: payload.action,
                                    selection: payload?.selection?.extraParams
                                })
                            }
                            return;
                        }
                        case "SERVICE:NAVIGATE:SYSTEM-MANAGER":
                        case "NAVIGATE:SYSTEM-MANAGER": {
                            navigateToSystemManagerFromService({ history, payload });
                            return;
                        }
                        case "OCCM-PROXY": {
                            dispatch({
                                type: 'SERVICE:OCCM-PROXY', payload
                            });
                            return;
                        }
                        case "SERVICE:ADD-NSS":
                        case "ADD-NSS": {
                            dispatch({
                                type: "SUPPORT:ADD-NSS-OPEN-POPOVER",
                                track: `from:${trackKey}`,
                                payload
                            })
                            return;
                        }
                        case "SERVICE:NOTIFICATION":
                        case "NOTIFICATION": {
                            dispatch({
                                type: payload.type,
                                payload: {
                                    message: payload.message,
                                    exception: payload.exception,
                                    group: payload.group
                                }
                            });
                            return;
                        }
                        case "OPEN-WIZARD": {
                            setWizardOpen(true);
                            return;
                        }
                        case "CLOSE-WIZARD": {
                            setWizardOpen(false);
                            return;
                        }
                        case "SEND-GA-EVENT": {
                            if(payload?.type === 'trackEvent') {
                                trackEvent({ ...payload?.details });
                            } else if(payload?.type === 'trackExternalLink') {
                                trackExternalLink({ ...payload?.details });
                            } else if(payload?.type === 'trackTiming') {
                                trackTiming({ ...payload?.details });
                            }
                            return;
                        }
                        case "SEND-FS-EVENT": {
                            trackFullStoryEvent({
                                name: payload.name, 
                                payload: payload.details
                            });
                            return;
                        }
                        default: {
                            const matched = listener(event.data);
                            if (!matched) {
                                console.log(`Parent APP received unknown message type - ${type}`);
                            }
                            return;
                        }
                    }
                }

            };

            window.addEventListener("message", receiveMessage, false);

            return () => {
                window.removeEventListener("message", receiveMessage)
            }
        }
    }, [messagePrefix, history, listener, shouldListen, dispatch, trackKey, location.state?.onReadyMessage, sendMessage]);

    useEffect(() => {
        if (apiResponses.length > 0) {
            const messageGroups = _.groupBy(apiResponses, "type");

            if (messageGroups["OCCM-PROXY-RESPONSE"]?.length > 0) {
                sendMessage({
                    type: "OCCM-PROXY-RESPONSE",
                    payload: { apiResponses: _.map(messageGroups["OCCM-PROXY-RESPONSE"], "payload") }
                })
            }

            if (messageGroups["NSS-ADDED"]?.length > 0) {
                sendMessage({
                    type: "NSS-ADDED"
                })
            }

            if (messageGroups["ADD-NSS-FAILED"]?.length > 0) {
                sendMessage({
                    type: "ADD-NSS-FAILED"
                })
            }

            dispatch({
                type: 'OCCM-PROXY:RESPONSES-HANDLED', payload: apiResponses
            });
        }

    }, [sendMessage, apiResponses, dispatch]);

    useEffect(() => {
        sendMessage({
            type: "CONNECTOR-CHANGE",
            payload: {
                connectorId,
                connectorName
            }
        })
    }, [sendMessage, connectorId, connectorName]);

    useEffect(() => {
        sendMessage({
            type: "WORKSPACE-CHANGE",
            payload: {
                workspaceId,
                scopeId
            }
        })
    }, [sendMessage, workspaceId]);

    useEffect(() => {
        sendMessage({
            type: "TOKEN-UPDATE",
            payload: {
                accessToken,
                userMetadata
            }
        })
    }, [sendMessage, accessToken, userMetadata]);

    const { pathname, hash, search, state } = location
    useEffect(() => {
        sendMessage({
            type: "LOCATION-CHANGE",
            payload: { pathname, hash, search, featureId, state }
        })
    }, [sendMessage, pathname, hash, search, featureId, state]);

    return { initializationData: initializationData.current }
};